import type { DragEndEvent } from "@dnd-kit/core";
import { SettingOutlined } from "@ant-design/icons";
import type { ListColumn } from "../list/listColumn";
import { closestCenter,DndContext,KeyboardSensor,PointerSensor,useSensor,useSensors } from "@dnd-kit/core";
import { arrayMove,SortableContext,sortableKeyboardCoordinates,verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button,Flex,Input,Space,theme,Tooltip,Typography } from "../ui";
import React from "react";
import { SortableColumnItem } from "./SortableColumnItem";
import { ThemedModal } from "../components/ThemedModal";
import type { LocalizedText } from "./settingsListTypes";

type ColumnConfig = {
  key: string;
  visible: boolean;
  order: number;
};

export type ColumnDefinition = {
  key: string;
  /** Optional: nur Fallback, wenn allColumnsMap[key].title kein String ist. Ansonsten wird die Tabellen-Überschrift (title) als einzige Quelle genutzt. */
  label?: LocalizedText;
  defaultVisible?: boolean;
  /**
   * Frühere oder technisch abweichende Schlüssel derselben logischen Spalte.
   * Beim Einlesen gespeicherter Einstellungen werden sie auf `key` zusammengeführt;
   * war einer der alten Einträge ausgeblendet, bleibt die logische Spalte ausgeblendet.
   */
  legacyKeys?: string[];
};

function normalizeColumnConfig(definitions: ColumnDefinition[], storedConfig: ColumnConfig[]): ColumnConfig[] {
  const validKeys = new Set(definitions.map((definition) => definition.key));
  const canonicalKeyByStoredKey = new Map<string, string>();
  definitions.forEach((definition) => {
    canonicalKeyByStoredKey.set(definition.key, definition.key);
    definition.legacyKeys?.forEach((legacyKey) => canonicalKeyByStoredKey.set(legacyKey, definition.key));
  });

  const normalizedByKey = new Map<string, ColumnConfig>();
  storedConfig.forEach((config) => {
    const canonicalKey = canonicalKeyByStoredKey.get(config.key);
    if (!canonicalKey || !validKeys.has(canonicalKey)) return;
    const existing = normalizedByKey.get(canonicalKey);
    normalizedByKey.set(canonicalKey, {
      key: canonicalKey,
      visible: existing ? existing.visible && config.visible : config.visible,
      order: existing ? Math.min(existing.order, config.order) : config.order,
    });
  });

  const normalized = Array.from(normalizedByKey.values());
  const existingKeys = new Set(normalized.map((config) => config.key));
  const missing = definitions
    .filter((definition) => !existingKeys.has(definition.key))
    .map((definition, index) => ({
      key: definition.key,
      visible: definition.defaultVisible ?? true,
      order: normalized.length + index,
    }));
  return [...normalized, ...missing]
    .sort((a, b) => a.order - b.order)
    .map((config, index) => ({ ...config, order: index }));
}

/**
 * Hook für wiederverwendbaren Spalten-Editor bei ProTable Listen.
 * Die Anzeige-Labels im Editor kommen aus allColumnsMap[key].title (gleiche Quelle wie die Tabellenkopfzeile).
 *
 * @param storageKey - Eindeutiger Key für localStorage (z.B. "rest-period-templates-columns")
 * @param columnDefinitions - Array mit key, optional label (Fallback), defaultVisible
 * @param allColumnsMap - Map mit allen Spalten (key -> ProColumn); title = Spaltenüberschrift = Anzeige im Editor
 * @param text - i18n Übersetzungsfunktion
 * @returns { visibleColumns, settingsButton, modal } - Komponenten für ProTable
 */
export function useProTableColumnEditor<T extends object>(
  storageKey: string,
  columnDefinitions: ColumnDefinition[],
  allColumnsMap: Record<string, ListColumn<T>>,
  text: (value: LocalizedText) => string
) {
  // Normierung statt frühem Throw – sonst wechselnde Hook-Anzahl (0 vs. N) → React #310
  const defs = Array.isArray(columnDefinitions) ? columnDefinitions : [];
  const columnsMap = typeof allColumnsMap === "object" && allColumnsMap !== null ? allColumnsMap : ({} as Record<string, ListColumn<T>>);

  const { token } = theme.useToken();
  const [columnSettingsOpen, setColumnSettingsOpen] = React.useState(false);
  const [columnSearchTerm, setColumnSearchTerm] = React.useState("");

  const savedColumnsState = localStorage.getItem(storageKey);
  const [columnConfig, setColumnConfig] = React.useState<ColumnConfig[]>(() => {
    if (savedColumnsState) {
      try {
        const parsed = JSON.parse(savedColumnsState) as ColumnConfig[];
        return normalizeColumnConfig(defs, parsed);
      } catch {
        return normalizeColumnConfig(defs, []);
      }
    }
    return normalizeColumnConfig(defs, []);
  });

  const columnConfigRef = React.useRef(columnConfig);
  React.useEffect(() => {
    columnConfigRef.current = columnConfig;
  }, [columnConfig]);

  // Dynamische Definitionen, entfernte Keys und technische Alias-Keys auf dieselbe kanonische Config bringen.
  const definitionKeys = defs
    .map((definition) => `${definition.key}:${(definition.legacyKeys ?? []).join("|")}`)
    .join(",");
  React.useEffect(() => {
    const prev = columnConfigRef.current;
    let stored = prev;
    const persisted = localStorage.getItem(storageKey);
    if (persisted) {
      try {
        stored = JSON.parse(persisted) as ColumnConfig[];
      } catch {
        stored = prev;
      }
    }
    const next = normalizeColumnConfig(defs, stored);
    if (JSON.stringify(prev) === JSON.stringify(next)) return;
    setColumnConfig(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  }, [definitionKeys, storageKey]);

  const saveColumnConfig = React.useCallback((config: ColumnConfig[]) => {
    setColumnConfig(config);
    localStorage.setItem(storageKey, JSON.stringify(config));
  }, [storageKey]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columnConfig.findIndex((c) => c.key === active.id);
    const newIndex = columnConfig.findIndex((c) => c.key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const newConfig = arrayMove(columnConfig, oldIndex, newIndex).map((c, idx) => ({ ...c, order: idx }));
    saveColumnConfig(newConfig);
  };

  const toggleColumnVisibility = (key: string) => {
    const newConfig = columnConfig.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c));
    saveColumnConfig(newConfig);
  };

  // Eine Quelle: Anzeige = allColumnsMap[key].title (wie in der Tabelle); Fallback = columnDefinitions[].label
  const columnDisplayLabels = React.useMemo(() => {
    return defs.reduce((acc, def) => {
      const col = columnsMap[def.key];
      const title = col?.title;
      if (typeof title === "string" && title.length > 0) {
        acc[def.key] = title;
      } else if (def.label != null) {
        acc[def.key] = text(def.label);
      } else {
        acc[def.key] = text("Column: {0}").replace("{0}", def.key);
      }
      return acc;
    }, {} as Record<string, string>);
  }, [defs, columnsMap, text]);

  const filteredColumns = columnConfig.filter((col) => {
    const label = columnDisplayLabels[col.key] ?? text("Column: {0}").replace("{0}", col.key);
    return label.toLowerCase().includes(columnSearchTerm.toLowerCase());
  });

  const visibleColumns = React.useMemo(() => {
    return columnConfig
      .filter((c) => c.visible)
      .sort((a, b) => a.order - b.order)
      .map((c) => {
        const col = columnsMap[c.key];
        if (!col) return col;
        // Der Konfigurations-Schlüssel ist die Wahrheit: Spalten ohne eigenes `key` bekamen sonst
        // `undefined` und wurden nachgelagert (SettingsListPage) nicht mehr in allColumnsMap
        // gefunden — Ergebnis war eine Spalte ganz ohne Überschrift und ohne Inhalt.
        return col.key != null ? col : { ...col, key: c.key };
      })
      .filter(Boolean);
  }, [columnConfig, columnsMap]);

  const settingsButtonLabel = text("Customize columns");
  const settingsButton = React.useMemo(() => (
    // Strecken statt height: 100% — in der umbrechenden Werkzeugleiste bezog sich die Prozenthöhe auf die
    // Leiste selbst, der Knopf rutschte in die zweite Zeile und ragte dort in die Liste.
    <Flex justify="flex-end" align="center" style={{ alignSelf: "stretch" }}>
      <Tooltip title={settingsButtonLabel}>
        <Button
          type="text"
          size="small"
          icon={<SettingOutlined />}
          aria-label={settingsButtonLabel}
          title={settingsButtonLabel}
          onClick={() => setColumnSettingsOpen(true)}
          style={{ color: token.colorTextSecondary, padding: 0, height: "auto" }}
        />
      </Tooltip>
    </Flex>
  ), [settingsButtonLabel, token.colorTextSecondary]);

  const modal = (
    <ThemedModal
      title={text("Customize columns")}
      open={columnSettingsOpen}
      onCancel={() => {
        setColumnSettingsOpen(false);
        setColumnSearchTerm("");
      }}
      footer={[
        <Button key="close" type="primary" onClick={() => {
          setColumnSettingsOpen(false);
          return setColumnSearchTerm("");
        }}>
          {text("Done")}
        </Button>,
      ]}
      width={500}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Input
          placeholder={text("Search columns...")}
          value={columnSearchTerm}
          onChange={(e) => setColumnSearchTerm(e.target.value)}
          onPressEnter={(e) => e.preventDefault()}
          allowClear
        />
        <Typography.Text type="secondary">
          {text("Select columns to display and drag them to change the order.")}
        </Typography.Text>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filteredColumns.map((c) => c.key)} strategy={verticalListSortingStrategy}>
            {filteredColumns.map((col) => (
              <SortableColumnItem
                key={col.key}
                id={col.key}
                label={columnDisplayLabels[col.key] ?? text("Column: {0}").replace("{0}", col.key)}
                visible={col.visible}
                onToggle={() => toggleColumnVisibility(col.key)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Space>
    </ThemedModal>
  );

  return {
    visibleColumns,
    settingsButton,
    modal,
  };
}








